import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { X } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SmoothScroll } from "@/components/site/primitives";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ErrorBoundary from "@/components/site/ErrorBoundary";
import { api } from "@/lib/api";
import Home from "@/pages/Home";
import Entertainment from "@/pages/Entertainment";
import Tickets from "@/pages/Tickets";
import GetInvolved from "@/pages/GetInvolved";
import Faq from "@/pages/Faq";
import SponsorDeck from "@/pages/SponsorDeck";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
}

function AnnouncementBar() {
  const [ann, setAnn] = useState(null);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    api.get("/announcement").then((r) => setAnn(r.data)).catch(() => {});
  }, []);
  if (!ann?.enabled || closed) return null;
  return (
    <div
      data-testid="announcement-bar"
      className="relative z-[55] bg-[#ffe600] text-black font-head font-bold text-center text-sm py-2 px-8"
    >
      <span className="badge-skew bg-[#ff0055] text-white px-2 py-0.5 mr-2 text-xs"><span>THWIP!</span></span>
      {ann.message}
      <button
        data-testid="announcement-close"
        onClick={() => setClosed(true)}
        className="absolute right-3 top-1/2 -translate-y-1/2"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null)
    return <div className="min-h-screen grid place-items-center font-display text-3xl text-[#00f0ff]">LOADING…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function SiteLayout({ children }) {
  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <Routes location={location}>
      <Route path="/" element={<SiteLayout><Home /></SiteLayout>} />
      <Route path="/entertainment" element={<SiteLayout><Entertainment /></SiteLayout>} />
      <Route path="/tickets" element={<SiteLayout><Tickets /></SiteLayout>} />
      <Route path="/get-involved" element={<SiteLayout><GetInvolved /></SiteLayout>} />
      <Route path="/faq" element={<SiteLayout><Faq /></SiteLayout>} />
      <Route path="/sponsor-deck" element={<SiteLayout><SponsorDeck /></SiteLayout>} />
      <Route path="/payment/success" element={<SiteLayout><PaymentSuccess /></SiteLayout>} />
      <Route path="/payment/cancel" element={<SiteLayout><PaymentCancel /></SiteLayout>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <SmoothScroll />
            <ScrollToTop />
            <AnimatedRoutes />
            <Toaster position="top-center" theme="dark" />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
